import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { type Me } from "@/lib/backend/api";
import { logger } from "@/logger";
import { queryOptions_me } from "../providers/query/functions/user/me";
import { useSessionProvider } from "../providers/session/use-session";

const SESSION_TIMEOUT_MS = 3000;

export function useAuthType() {
  const { session } = useSessionProvider();
  const [sessionTimedOut, setSessionTimedOut] = useState(false);

  // Timeout mechanism: If session stays pending for too long, force it to resolve as no session
  useEffect(() => {
    if (!session.isPending) {
      setSessionTimedOut(false);
      return;
    }

    const timer = setTimeout(() => {
      if (session.isPending) {
        logger.warn(
          {
            timeout: SESSION_TIMEOUT_MS,
            sessionState: session.isPending,
          },
          "Session check timed out - treating as unauthenticated",
        );
        setSessionTimedOut(true);
      }
    }, SESSION_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [session.isPending]);

  const {
    data: me,
    isLoading: isLoadingMe,
    isError: isErrorMe,
    refetch: refetchMe,
  } = useQuery({
    ...queryOptions_me({}),
    enabled: !!session.data,
  });

  const isAdmin = me?.adminUser !== null && !isLoadingMe;

  // Only show loading on initial load, not on refetches
  // This prevents white screen when invalidating queries (e.g., profile picture upload)
  // If there's an error, don't show loading to prevent infinite loading state
  // If session timed out, don't show loading (treat as no session)
  const isLoading =
    (session.isPending && !sessionTimedOut) || (!!session.data && isLoadingMe && !me && !isErrorMe);

  // If session timed out, treat as unauthenticated
  const isSignedIn = sessionTimedOut ? false : !!session.data;
  const isEmployee = sessionTimedOut ? false : !!session.data && (me as Me)?.employee !== null;

  return {
    isLoading,
    isSignedIn,
    isEmployee,
    isAdmin,
    me,
    refetchMe,
    session,
    isLoadingMe,
  };
}
